import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, fetchInvite } from "../hooks/useAuth";
import { useEffect, useState } from "react";

const NAVY = "#0B1F4A";
const NAVY_MID = "#1e3f82";
const ORANGE = "#C83803";
const ORANGE_DARK = "#7a1e00";
const WHITE = "#FFFFFF";
const MUTED = "rgba(255,255,255,0.55)";

const responsiveStyles = `
  .hero-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 48px; align-items: center; max-width: 1100px; margin: 0 auto; padding: 72px 48px 64px; }
  .steps-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 28px; max-width: 1100px; margin: 0 auto; }
  .features-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; max-width: 1100px; margin: 0 auto; }
  .stats-row { display: flex; justify-content: center; gap: 48px; flex-wrap: wrap; padding: 40px 48px; }
  .section-inner { max-width: 1100px; margin: 0 auto; padding: 0 48px; }

  @media (max-width: 860px) {
    .hero-grid { grid-template-columns: 1fr; padding: 48px 24px 40px; gap: 32px; }
    .demo-card { display: none; }
    .steps-grid { grid-template-columns: 1fr 1fr; gap: 20px; }
    .features-grid { grid-template-columns: 1fr 1fr; }
    .stats-row { gap: 28px; padding: 32px 24px; }
    .section-inner { padding: 0 24px; }
    .nav-pad { padding: 16px 24px !important; }
    .footer-pad { padding: 20px 24px !important; }
    .section-pad { padding: 56px 24px !important; }
  }

  @media (max-width: 540px) {
    .steps-grid { grid-template-columns: 1fr; }
    .features-grid { grid-template-columns: 1fr; }
    .stats-row { gap: 20px; }
    .hero-h1 { font-size: 36px !important; letter-spacing: -1px !important; }
    .hero-sub { font-size: 15px !important; }
    .cta-big { font-size: 15px !important; padding: 14px 26px !important; }
  }
`;

export default function Landing() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteId = searchParams.get("invite");

  const [invite, setInvite] = useState(null);
  const [inviteLoading, setInviteLoading] = useState(!!inviteId);
  const [inviteLoadError, setInviteLoadError] = useState(false);

  const [authModalOpen, setAuthModalOpen] = useState(false);

  // Load invite details if there's an ?invite=... param
  useEffect(() => {
    if (!inviteId) return;
    let cancelled = false;
    fetchInvite(inviteId).then(inv => {
      if (cancelled) return;
      if (!inv) { setInviteLoadError(true); }
      setInvite(inv);
      setInviteLoading(false);
    });
    return () => { cancelled = true; };
  }, [inviteId]);

  // Redirect to dashboard when signed in, UNLESS there's an invite mismatch
  // (we stay on this page to show the mismatch UI)
  useEffect(() => {
    if (!user) return;
    if (inviteLoading) return;
    if (invite && user.email && invite.email.toLowerCase() !== user.email.toLowerCase()) {
      // Stay here — show mismatch screen
      return;
    }
    navigate("/dashboard");
  }, [user, invite, inviteLoading, navigate]);

  const openAuth = () => setAuthModalOpen(true);

  // ── Invite mode: replace the marketing landing with a focused "accept invite" screen

  if (inviteId && inviteLoading) {
    return <FullscreenMessage title="Loading invitation..." />;
  }

  if (inviteId && inviteLoadError) {
    return (
      <FullscreenMessage
        title="Invitation not found"
        body="This invitation link is no longer valid. It may have been canceled or already used. Ask whoever invited you to send a new one."
        ctaLabel="Go to LifeTrackr"
        onCta={() => { window.location.href = "/"; }}
      />
    );
  }

  if (invite && user && user.email?.toLowerCase() !== invite.email.toLowerCase()) {
    return (
      <FullscreenMessage
        title="Wrong account"
        body={<>This invitation was sent to <strong>{invite.email}</strong>, but you're signed in as <strong>{user.email}</strong>. Sign out and sign in with the invited email to accept.</>}
        ctaLabel="Sign out"
        onCta={async () => { await logout(); window.location.reload(); }}
      />
    );
  }

  if (invite && !user) {
    return (
      <>
        <InviteAcceptScreen invite={invite} onOpenAuth={openAuth} />
        {authModalOpen && <AuthModal onClose={() => setAuthModalOpen(false)} lockedEmail={invite.email} diaryName={invite.diaryName} />}
      </>
    );
  }

  // ── Normal marketing landing page

  return (
    <div style={{ background: NAVY, color: WHITE, fontFamily: "'Georgia', serif", minHeight: "100vh" }}>
      <style>{responsiveStyles}</style>

      {/* NAV */}
      <nav className="nav-pad" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 48px", background: ORANGE, borderBottom: `3px solid ${ORANGE_DARK}` }}>
        <span style={{ fontSize: "28px", fontWeight: "900", color: WHITE }}>Life<span style={{ color: NAVY }}>Trackr</span></span>
        <button onClick={openAuth} style={{ background: NAVY, color: WHITE, border: "none", padding: "11px 22px", borderRadius: "6px", fontSize: "14px", fontWeight: "700", cursor: "pointer", fontFamily: "'Georgia', serif", whiteSpace: "nowrap" }}>
          Get started free
        </button>
      </nav>

      {/* HERO */}
      <div style={{ background: `linear-gradient(160deg, ${NAVY_MID} 0%, ${NAVY} 50%, #071530 100%)`, borderBottom: `3px solid ${ORANGE}` }}>
        <div className="hero-grid">
          <div>
            <div style={{ display: "inline-block", background: "rgba(200,56,3,0.15)", border: "1px solid rgba(200,56,3,0.4)", color: "#ff7a4d", padding: "4px 14px", borderRadius: "4px", fontSize: "11px", fontWeight: "700", letterSpacing: "1px", textTransform: "uppercase", marginBottom: "24px" }}>
              Now in early access
            </div>
            <h1 className="hero-h1" style={{ fontSize: "clamp(36px, 5vw, 58px)", fontWeight: "900", lineHeight: 1.05, letterSpacing: "-1.5px", margin: "0 0 20px", color: WHITE, textShadow: `-2px -2px 0 ${NAVY},-2px 2px 0 ${NAVY},2px -2px 0 ${NAVY},2px 2px 0 ${NAVY},4px 4px 0 rgba(0,0,0,0.4)` }}>
              Your life's moments,<br /><em style={{ fontStyle: "italic", color: ORANGE, textShadow: `-2px -2px 0 ${ORANGE_DARK},-2px 2px 0 ${ORANGE_DARK},2px -2px 0 ${ORANGE_DARK},2px 2px 0 ${ORANGE_DARK},4px 4px 0 rgba(0,0,0,0.4)` }}>captured by email.</em>
            </h1>
            <p className="hero-sub" style={{ fontSize: "17px", color: MUTED, lineHeight: 1.75, margin: "0 0 32px", maxWidth: "460px" }}>
              Send an email. Add a photo. Watch it appear in a beautiful diary your whole family can share. No apps to open. No habits to build.
            </p>
            <button className="cta-big" onClick={openAuth} style={{ display: "inline-flex", alignItems: "center", gap: "10px", background: ORANGE, color: WHITE, border: "none", padding: "16px 32px", borderRadius: "6px", fontSize: "16px", fontWeight: "700", cursor: "pointer", fontFamily: "'Georgia', serif", boxShadow: "0 4px 20px rgba(200,56,3,0.35)" }}>
              Start your diary free →
            </button>
            <p style={{ marginTop: "14px", fontSize: "13px", color: "rgba(255,255,255,0.3)" }}>Free forever · No credit card required</p>
          </div>

          <div className="demo-card" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "16px", overflow: "hidden", boxShadow: "0 32px 64px rgba(0,0,0,0.4)" }}>
            <div style={{ background: "rgba(255,255,255,0.05)", borderBottom: "1px solid rgba(255,255,255,0.08)", padding: "12px 20px", display: "flex", alignItems: "center", gap: "8px" }}>
              {["#ff5f57","#ffbd2e","#28ca41"].map(c => <div key={c} style={{ width: 10, height: 10, borderRadius: "50%", background: c }} />)}
              <span style={{ fontSize: "12px", color: MUTED, marginLeft: "8px" }}>Lucas — LifeTrackr</span>
            </div>
            <div style={{ padding: "24px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "14px", marginBottom: "20px" }}>
                <div style={{ width: "44px", height: "44px", borderRadius: "10px", background: "rgba(200,56,3,0.2)", border: "1px solid rgba(200,56,3,0.35)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "20px", fontWeight: "700", color: ORANGE }}>L</div>
                <div>
                  <div style={{ fontSize: "16px", fontWeight: "700", color: WHITE }}>Lucas</div>
                  <div style={{ fontSize: "11px", color: ORANGE, opacity: 0.7, fontStyle: "italic" }}>lucas-a3f9@lifetrackr.app</div>
                </div>
              </div>
              {[
                { date: "April 16, 2026 · 10:07 AM", text: "Found out Lucas has a prom date. Working on the poster all week. So proud! 🎉" },
                { date: "April 5, 2026 · 12:00 AM", text: "Spring break — Philly, Annapolis, Baltimore and DC. Best family week in years." },
                { date: "March 22, 2026 · 12:00 AM", text: "Riley played UTR tourney. Blown away by how good the boys were." },
              ].map((e, i) => (
                <div key={i}>
                  {i > 0 && <div style={{ height: 1, background: "rgba(255,255,255,0.07)", margin: "14px 0" }} />}
                  <span style={{ fontSize: "11px", color: ORANGE, fontStyle: "italic", display: "block", marginBottom: "6px" }}>{e.date}</span>
                  <p style={{ fontSize: "13px", color: "rgba(255,255,255,0.75)", lineHeight: 1.65, margin: 0 }}>{e.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* STATS */}
      <div className="stats-row" style={{ background: NAVY, borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
        {[
          { num: "2 min", label: "average setup time" },
          { num: "< 60s", label: "email to diary entry" },
          { num: "∞", label: "memories stored" },
          { num: "0", label: "new habits required" },
        ].map((s, i) => (
          <div key={i} style={{ textAlign: "center" }}>
            <div style={{ fontSize: "28px", fontWeight: "900", color: ORANGE, fontFamily: "'Georgia', serif" }}>{s.num}</div>
            <div style={{ fontSize: "13px", color: MUTED }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* HOW IT WORKS */}
      <div className="section-pad" style={{ background: "rgba(255,255,255,0.02)", borderTop: "1px solid rgba(255,255,255,0.06)", borderBottom: "1px solid rgba(255,255,255,0.06)", padding: "72px 48px" }}>
        <div className="section-inner">
          <div style={{ fontSize: "11px", fontWeight: "700", letterSpacing: "2px", textTransform: "uppercase", color: ORANGE, marginBottom: "14px" }}>How it works</div>
          <h2 style={{ fontSize: "clamp(26px, 4vw, 42px)", fontWeight: "900", letterSpacing: "-1px", marginBottom: "48px", color: WHITE, textShadow: `-1.5px -1.5px 0 ${NAVY},1.5px -1.5px 0 ${NAVY},-1.5px 1.5px 0 ${NAVY},1.5px 1.5px 0 ${NAVY},3px 3px 0 rgba(0,0,0,0.3)` }}>
            Four steps to a memory that lasts forever.
          </h2>
          <div className="steps-grid">
            {[
              { n: "01", title: "Create a diary", desc: "Name it after a person or chapter of your life. Get a unique inbound email instantly." },
              { n: "02", title: "Send an email", desc: "Write your memory in the body. Attach photos. Backdate with a simple subject tag." },
              { n: "03", title: "It's in your diary", desc: "Your entry appears instantly, date-stamped, with photos embedded." },
              { n: "04", title: "Share with family", desc: "Invite grandparents, spouses, kids. Everyone can view and contribute." },
            ].map((s, i) => (
              <div key={i}>
                <div style={{ fontSize: "44px", fontWeight: "900", color: "rgba(200,56,3,0.25)", lineHeight: 1, marginBottom: "14px", fontFamily: "'Georgia', serif" }}>{s.n}</div>
                <div style={{ fontSize: "15px", fontWeight: "700", marginBottom: "8px", color: WHITE }}>{s.title}</div>
                <p style={{ fontSize: "14px", color: MUTED, lineHeight: 1.7, margin: 0 }}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* FEATURES */}
      <div className="section-pad" style={{ background: NAVY, padding: "72px 48px" }}>
        <div className="section-inner">
          <div style={{ fontSize: "11px", fontWeight: "700", letterSpacing: "2px", textTransform: "uppercase", color: ORANGE, marginBottom: "14px" }}>Features</div>
          <h2 style={{ fontSize: "clamp(26px, 4vw, 42px)", fontWeight: "900", letterSpacing: "-1px", marginBottom: "40px", color: WHITE, textShadow: `-1.5px -1.5px 0 ${NAVY},1.5px -1.5px 0 ${NAVY},-1.5px 1.5px 0 ${NAVY},1.5px 1.5px 0 ${NAVY},3px 3px 0 rgba(0,0,0,0.3)` }}>
            Everything a memory deserves.
          </h2>
          <div className="features-grid">
            {[
              { icon: "✉️", title: "Email-first logging", desc: "Log from any device, any email client, anywhere. No app to open." },
              { icon: "📸", title: "Photos embedded", desc: "Attach photos to any email. They appear inline in your diary." },
              { icon: "👨‍👩‍👧‍👦", title: "Family sharing", desc: "Invite anyone as a viewer or contributor. Build it together." },
              { icon: "📅", title: "Backdating", desc: "Use (-2) in subject for 2 days ago, or (-2026-04-01) for any date." },
              { icon: "📬", title: "Weekly digest", desc: "Every Sunday, subscribers get a beautiful email of the week's entries." },
              { icon: "📚", title: "Multiple diaries", desc: "Create separate diaries for each child, each year, each chapter." },
            ].map((f, i) => (
              <div key={i} style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: "12px", padding: "22px" }}>
                <span style={{ fontSize: "26px", display: "block", marginBottom: "12px" }}>{f.icon}</span>
                <div style={{ fontSize: "15px", fontWeight: "700", marginBottom: "7px", color: WHITE }}>{f.title}</div>
                <p style={{ fontSize: "13px", color: MUTED, lineHeight: 1.65, margin: 0 }}>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="section-pad" style={{ background: NAVY, textAlign: "center", padding: "80px 24px", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
        <h2 style={{ fontSize: "clamp(28px, 4vw, 48px)", fontWeight: "900", letterSpacing: "-1px", margin: "0 auto 16px", maxWidth: "600px", color: WHITE, textShadow: `-1.5px -1.5px 0 ${NAVY},1.5px -1.5px 0 ${NAVY},-1.5px 1.5px 0 ${NAVY},1.5px 1.5px 0 ${NAVY},3px 3px 0 rgba(0,0,0,0.3)` }}>
          Start capturing your story today.
        </h2>
        <p style={{ fontSize: "17px", color: MUTED, marginBottom: "36px" }}>Free forever for one diary. No credit card. Takes two minutes.</p>
        <button className="cta-big" onClick={openAuth} style={{ background: ORANGE, color: WHITE, border: "none", padding: "18px 40px", borderRadius: "6px", fontSize: "18px", fontWeight: "700", cursor: "pointer", fontFamily: "'Georgia', serif", boxShadow: "0 4px 24px rgba(200,56,3,0.35)" }}>
          Create your first diary →
        </button>
      </div>

      {/* FOOTER */}
      <footer className="footer-pad" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "22px 48px", background: "#071530", borderTop: "1px solid rgba(255,255,255,0.07)", flexWrap: "wrap", gap: "8px" }}>
        <span style={{ fontSize: "20px", fontWeight: "900", color: WHITE }}>Life<span style={{ color: ORANGE }}>Trackr</span></span>
        <span style={{ fontSize: "13px", color: "rgba(255,255,255,0.25)" }}>© 2026 LifeTrackr. Built for families who want to remember.</span>
      </footer>

      {/* AUTH MODAL */}
      {authModalOpen && <AuthModal onClose={() => setAuthModalOpen(false)} />}
    </div>
  );
}

// ── Invite accept screen ──────────────────────────────────────────────────────

function InviteAcceptScreen({ invite, onOpenAuth }) {
  const roleLabel = invite.role === "contributor" ? "Editor" : "Viewer";
  return (
    <div style={{ background: NAVY, color: WHITE, fontFamily: "'Georgia', serif", minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <nav style={{ padding: "18px 48px", background: ORANGE, borderBottom: `3px solid ${ORANGE_DARK}` }}>
        <span style={{ fontSize: "28px", fontWeight: "900", color: WHITE }}>Life<span style={{ color: NAVY }}>Trackr</span></span>
      </nav>
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "48px 24px", background: `linear-gradient(160deg, ${NAVY_MID} 0%, ${NAVY} 50%, #071530 100%)` }}>
        <div style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: "16px", padding: "44px 36px", maxWidth: "460px", width: "100%", textAlign: "center", boxShadow: "0 20px 60px rgba(0,0,0,0.4)" }}>
          <div style={{ fontSize: "48px", marginBottom: "16px" }}>📖</div>
          <h1 style={{ fontSize: "26px", fontWeight: "900", lineHeight: 1.2, margin: "0 0 12px", color: WHITE }}>You've been invited!</h1>
          <p style={{ fontSize: "15px", color: MUTED, lineHeight: 1.7, margin: "0 0 28px" }}>
            <strong style={{ color: WHITE }}>{invite.invitedByName || "Someone"}</strong> invited you to share the diary <strong style={{ color: ORANGE }}>{invite.diaryName}</strong> as {/^[aeiou]/i.test(roleLabel) ? "an" : "a"} <strong style={{ color: WHITE }}>{roleLabel}</strong>.
          </p>
          <div style={{ padding: "12px 16px", background: "rgba(200,56,3,0.08)", border: "1px solid rgba(200,56,3,0.25)", borderRadius: "8px", marginBottom: "24px" }}>
            <div style={{ fontSize: "11px", color: "rgba(255,255,255,0.5)", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "4px" }}>Invited email</div>
            <div style={{ fontSize: "14px", color: WHITE, fontWeight: 600 }}>{invite.email}</div>
          </div>
          <button onClick={onOpenAuth}
            style={{ width: "100%", background: ORANGE, color: WHITE, border: "none", padding: "14px", borderRadius: "6px", fontSize: "15px", fontWeight: "700", cursor: "pointer", fontFamily: "'Georgia', serif", boxShadow: "0 4px 20px rgba(200,56,3,0.35)" }}>
            Sign in to accept
          </button>
          <p style={{ fontSize: "12px", color: "rgba(255,255,255,0.35)", marginTop: "16px", lineHeight: 1.5 }}>
            You'll need to sign in with <strong>{invite.email}</strong> to join this diary.
          </p>
        </div>
      </div>
    </div>
  );
}

// ── Full-screen status message ────────────────────────────────────────────────

function FullscreenMessage({ title, body, ctaLabel, onCta }) {
  return (
    <div style={{ background: NAVY, color: WHITE, fontFamily: "'Georgia', serif", minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <nav style={{ padding: "18px 48px", background: ORANGE, borderBottom: `3px solid ${ORANGE_DARK}` }}>
        <span style={{ fontSize: "28px", fontWeight: "900", color: WHITE }}>Life<span style={{ color: NAVY }}>Trackr</span></span>
      </nav>
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "48px 24px" }}>
        <div style={{ maxWidth: "440px", textAlign: "center" }}>
          <h1 style={{ fontSize: "24px", fontWeight: "900", margin: "0 0 12px", color: WHITE }}>{title}</h1>
          {body && <p style={{ fontSize: "15px", color: MUTED, lineHeight: 1.7, margin: "0 0 24px" }}>{body}</p>}
          {ctaLabel && (
            <button onClick={onCta}
              style={{ background: ORANGE, color: WHITE, border: "none", padding: "12px 28px", borderRadius: "6px", fontSize: "14px", fontWeight: "700", cursor: "pointer", fontFamily: "'Georgia', serif" }}>
              {ctaLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Auth modal (sign in or sign up, Google + email/password) ──────────────────

function AuthModal({ onClose, lockedEmail, diaryName }) {
  const { signInWithGoogle, signUpWithEmail, signInWithEmail, resetPassword } = useAuth();
  const [mode, setMode] = useState(lockedEmail ? "signup" : "signin");
  const [email, setEmail] = useState(lockedEmail || "");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [resetSent, setResetSent] = useState(false);

  const friendlyError = (code) => {
    switch (code) {
      case "auth/invalid-email": return "That email address doesn't look right.";
      case "auth/user-not-found": return "No account exists with that email. Try signing up instead.";
      case "auth/wrong-password":
      case "auth/invalid-credential": return "Incorrect email or password.";
      case "auth/email-already-in-use": return "An account with that email already exists. Try signing in.";
      case "auth/weak-password": return "Password must be at least 6 characters.";
      case "auth/popup-closed-by-user": return null; // silent — user canceled
      default: return "Something went wrong. Please try again.";
    }
  };

  const handleGoogle = async () => {
    setError(null);
    try { await signInWithGoogle(); onClose(); }
    catch (e) { const m = friendlyError(e.code); if (m) setError(m); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setError(null);
    setBusy(true);
    try {
      if (mode === "signup") {
        await signUpWithEmail(email.trim().toLowerCase(), password, name.trim() || email.split("@")[0]);
      } else {
        await signInWithEmail(email.trim().toLowerCase(), password);
      }
      onClose();
    } catch (err) {
      const m = friendlyError(err.code);
      setError(m || "Something went wrong. Please try again.");
    }
    setBusy(false);
  };

  const handleReset = async () => {
    setError(null);
    if (!email.trim()) { setError("Enter your email above first."); return; }
    try {
      await resetPassword(email.trim().toLowerCase());
      setResetSent(true);
    } catch (err) {
      setError(friendlyError(err.code) || "Couldn't send reset email.");
    }
  };

  return (
    <div onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px", fontFamily: "-apple-system,sans-serif" }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: "white", color: "#333", borderRadius: "16px", padding: "32px", maxWidth: "420px", width: "100%", boxShadow: "0 20px 60px rgba(0,0,0,0.35)", maxHeight: "calc(100vh - 40px)", overflowY: "auto", boxSizing: "border-box" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
          <h2 style={{ fontSize: "22px", fontWeight: "700", color: NAVY, margin: 0, fontFamily: "'Georgia', serif" }}>
            {lockedEmail ? "Accept invitation" : mode === "signup" ? "Create account" : "Welcome back"}
          </h2>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: "24px", color: "#aaa", cursor: "pointer", padding: 0, lineHeight: 1 }}>×</button>
        </div>
        {lockedEmail && diaryName && (
          <p style={{ fontSize: "13px", color: "#888", lineHeight: 1.5, margin: "0 0 20px" }}>
            Sign in or create an account with <strong style={{ color: ORANGE }}>{lockedEmail}</strong> to join <strong>{diaryName}</strong>.
          </p>
        )}
        {!lockedEmail && (
          <p style={{ fontSize: "13px", color: "#888", margin: "0 0 20px" }}>
            {mode === "signup" ? "Start your diary in under a minute." : "Sign in to your diaries."}
          </p>
        )}

        {/* Google button */}
        <button onClick={handleGoogle} disabled={busy}
          style={{ width: "100%", background: "white", color: "#333", border: "1px solid #ddd", padding: "12px", borderRadius: "8px", fontSize: "14px", fontWeight: "600", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "10px", marginBottom: "16px", fontFamily: "-apple-system,sans-serif" }}>
          <svg width="18" height="18" viewBox="0 0 18 18"><path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/><path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z"/><path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/><path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/></svg>
          Continue with Google
        </button>

        {/* Divider */}
        <div style={{ display: "flex", alignItems: "center", gap: "10px", margin: "16px 0", color: "#aaa", fontSize: "12px" }}>
          <div style={{ flex: 1, height: "1px", background: "#eee" }} />
          or
          <div style={{ flex: 1, height: "1px", background: "#eee" }} />
        </div>

        {/* Email form */}
        <form onSubmit={handleSubmit}>
          {mode === "signup" && (
            <input type="text" value={name} onChange={e => setName(e.target.value)}
              placeholder="Your name (optional)"
              style={{ width: "100%", background: "#fafafa", border: "1px solid #ddd", borderRadius: "8px", padding: "11px 14px", fontSize: "14px", marginBottom: "10px", boxSizing: "border-box", outline: "none", fontFamily: "-apple-system,sans-serif" }} />
          )}
          <input type="email" value={email} onChange={e => setEmail(e.target.value)}
            placeholder="Email" required autoComplete="email"
            disabled={!!lockedEmail}
            style={{ width: "100%", background: lockedEmail ? "#f0f0f0" : "#fafafa", border: "1px solid #ddd", borderRadius: "8px", padding: "11px 14px", fontSize: "14px", marginBottom: "10px", boxSizing: "border-box", outline: "none", fontFamily: "-apple-system,sans-serif", color: lockedEmail ? "#888" : "#333" }} />
          <input type="password" value={password} onChange={e => setPassword(e.target.value)}
            placeholder={mode === "signup" ? "Password (at least 6 characters)" : "Password"}
            required autoComplete={mode === "signup" ? "new-password" : "current-password"}
            minLength={6}
            style={{ width: "100%", background: "#fafafa", border: "1px solid #ddd", borderRadius: "8px", padding: "11px 14px", fontSize: "14px", marginBottom: "10px", boxSizing: "border-box", outline: "none", fontFamily: "-apple-system,sans-serif" }} />

          {error && (
            <div style={{ background: "rgba(200,56,3,0.06)", border: "1px solid rgba(200,56,3,0.25)", color: "#c83803", borderRadius: "6px", padding: "8px 12px", fontSize: "13px", marginBottom: "10px" }}>
              {error}
            </div>
          )}
          {resetSent && (
            <div style={{ background: "rgba(42,122,58,0.08)", border: "1px solid rgba(42,122,58,0.25)", color: "#2a7a3a", borderRadius: "6px", padding: "8px 12px", fontSize: "13px", marginBottom: "10px" }}>
              Password reset email sent. Check your inbox.
            </div>
          )}

          <button type="submit" disabled={busy || !email || !password}
            style={{ width: "100%", background: (busy || !email || !password) ? "#ddd" : ORANGE, color: "white", border: "none", padding: "12px", borderRadius: "8px", fontSize: "14px", fontWeight: "700", cursor: (busy || !email || !password) ? "default" : "pointer", fontFamily: "-apple-system,sans-serif", marginBottom: "10px" }}>
            {busy ? "..." : mode === "signup" ? "Create account" : "Sign in"}
          </button>
        </form>

        <div style={{ fontSize: "13px", color: "#666", textAlign: "center", marginTop: "8px" }}>
          {mode === "signin" ? (
            <>
              <button type="button" onClick={handleReset} style={{ background: "none", border: "none", color: ORANGE, fontSize: "13px", cursor: "pointer", padding: 0, fontFamily: "-apple-system,sans-serif" }}>
                Forgot password?
              </button>
              <span style={{ color: "#ccc", margin: "0 8px" }}>·</span>
              <button type="button" onClick={() => { setMode("signup"); setError(null); }} style={{ background: "none", border: "none", color: ORANGE, fontSize: "13px", cursor: "pointer", padding: 0, fontWeight: 600, fontFamily: "-apple-system,sans-serif" }}>
                Create an account
              </button>
            </>
          ) : (
            <>
              Already have an account?{" "}
              <button type="button" onClick={() => { setMode("signin"); setError(null); }} style={{ background: "none", border: "none", color: ORANGE, fontSize: "13px", cursor: "pointer", padding: 0, fontWeight: 600, fontFamily: "-apple-system,sans-serif" }}>
                Sign in
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
